// SCR-CONSOLE-002، زبانه اشتراک (AP-PLT-07؛ C-AGENT-1170): وضعیت اشتراک و صورتحساب‌های مستأجر، تمدید Trial با دلیل (AC-PLT-021.3، platform.subscription.manage)،
// خاتمه مستأجر تعلیق‌شده (platform.tenant.suspend)، ساخت محیط نمایشی (US-PLT-053، platform.tenant.create)، تأیید یا رد انتقال مالکیت (US-PLT-007) و گواهی حذف
// امن (AC-PLT-006.3). بدون داده مراجعه‌کننده (AC-PLT-080.1).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type ConsoleTenantDetail, type OwnershipTransferView, type SubscriptionInvoiceView } from '@phoenix/api-client';
import { usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  KeyValueList,
  latinDigits,
  LoadingState,
  Money,
  ReasonDialog,
  toJalali,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { usePlatformAccess } from '../../access';
import { consoleKeys, toNumber } from '../../api';

export const subscriptionConsoleKeys = {
  overview: (id: string) => ['platform', 'console', 'tenant', id, 'subscription'] as const,
  invoices: (id: string) => ['platform', 'console', 'tenant', id, 'subscription-invoices'] as const,
  transfers: ['platform', 'console', 'ownership-transfers'] as const,
  certificates: (id: string) => ['platform', 'console', 'tenant', id, 'purge-certificates'] as const,
};

function isoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** انتقال‌های مالکیت منتظر تأیید (همه مستأجرها یا یک مستأجر). */
export function OwnershipApprovals({ tenantId }: { tenantId?: string }) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const access = usePlatformAccess();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState<OwnershipTransferView>();
  const transfers = useQuery({
    queryKey: subscriptionConsoleKeys.transfers,
    queryFn: () => unwrap(api.GET('/api/v1/platform/console/ownership-transfers')),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: subscriptionConsoleKeys.transfers });
  const approve = useMutation({
    mutationFn: (id: string) => unwrap(api.POST('/api/v1/platform/console/ownership-transfers/{id}:approve', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      unwrap(api.POST('/api/v1/platform/console/ownership-transfers/{id}:reject', { params: { path: { id } }, body: { reason } })),
    onSuccess: () => {
      setRejecting(undefined);
      refresh();
    },
  });
  const rows = (transfers.data?.items ?? []).filter((item) => !tenantId || item.tenantId === tenantId);
  if (!tenantId && rows.length === 0) {
    return null;
  }
  const canDecide = access.can('platform.tenant.suspend');
  const columns: DataTableColumn<OwnershipTransferView>[] = [
    { id: 'tenant', headerKey: 'console:subscription.transfers.tenant', cell: (row) => row.tenantName ?? '—' },
    { id: 'mobile', headerKey: 'console:subscription.transfers.mobile', cell: (row) => <bdi dir="ltr">{faDigits(row.newOwnerMobile)}</bdi> },
    { id: 'accepted', headerKey: 'console:subscription.transfers.accepted', cell: (row) => (row.acceptedAt ? <JalaliDate value={row.acceptedAt} withTime /> : '—') },
    { id: 'expires', headerKey: 'console:subscription.transfers.expires', cell: (row) => <JalaliDate value={row.expiresAt} withTime /> },
    {
      id: 'actions',
      headerKey: 'console:subscription.transfers.actions',
      cell: (row) =>
        canDecide ? (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" onClick={() => approve.mutate(row.id)} disabled={approve.isPending} data-testid={`transfer-approve-${row.id}`}>
              {t('console:subscription.transfers.approve')}
            </Button>
            <Button size="small" color="error" onClick={() => setRejecting(row)} data-testid={`transfer-reject-${row.id}`}>
              {t('console:subscription.transfers.reject')}
            </Button>
          </Stack>
        ) : null,
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="ownership-approvals">
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t('console:subscription.transfers.title')}
      </Typography>
      {approve.error ? <ErrorState error={approve.error} compact /> : null}
      <DataTable
        captionKey="console:subscription.transfers.title"
        columns={columns}
        rows={rows}
        isLoading={transfers.isPending}
        error={transfers.error}
        onRetry={() => void transfers.refetch()}
        getRowId={(row) => row.id}
        emptyTitleKey="console:subscription.transfers.empty"
      />
      <ReasonDialog
        open={Boolean(rejecting)}
        titleKey="console:subscription.transfers.reject_title"
        busy={reject.isPending}
        error={reject.error}
        onCancel={() => setRejecting(undefined)}
        onConfirm={(reason) => rejecting && reject.mutate({ id: rejecting.id, reason })}
      />
    </Paper>
  );
}

export function SubscriptionTab({ tenant }: { tenant: ConsoleTenantDetail }) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const access = usePlatformAccess();
  const queryClient = useQueryClient();
  const [extendOpen, setExtendOpen] = useState(false);
  const [days, setDays] = useState('7');
  const [terminateOpen, setTerminateOpen] = useState(false);
  const overview = useQuery({
    queryKey: subscriptionConsoleKeys.overview(tenant.id),
    queryFn: () => unwrap(api.GET('/api/v1/platform/console/tenants/{id}/subscription', { params: { path: { id: tenant.id } } })),
  });
  const invoices = useQuery({
    queryKey: subscriptionConsoleKeys.invoices(tenant.id),
    queryFn: () => unwrap(api.GET('/api/v1/platform/console/tenants/{id}/subscription/invoices', { params: { path: { id: tenant.id } } })),
  });
  const certificates = useQuery({
    queryKey: subscriptionConsoleKeys.certificates(tenant.id),
    queryFn: () => unwrap(api.GET('/api/v1/platform/console/purge-certificates', { params: { query: { tenantId: tenant.id } } })),
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: subscriptionConsoleKeys.overview(tenant.id) });
    await queryClient.invalidateQueries({ queryKey: consoleKeys.tenant(tenant.id) });
  };
  const daysValue = Number(latinDigits(days));
  const extend = useMutation({
    mutationFn: (reason: string) => {
      const endsOn = new Date();
      endsOn.setDate(endsOn.getDate() + daysValue);
      return unwrap(
        api.POST('/api/v1/platform/console/tenants/{id}/subscription:extend-trial', {
          params: { path: { id: tenant.id } },
          body: { endsOn: isoDate(endsOn), reason },
        }),
      );
    },
    onSuccess: async () => {
      setExtendOpen(false);
      await refresh();
    },
  });
  const terminate = useMutation({
    mutationFn: (reason: string) => unwrap(api.POST('/api/v1/platform/console/tenants/{id}:terminate', { params: { path: { id: tenant.id } }, body: { reason } })),
    onSuccess: async () => {
      setTerminateOpen(false);
      await refresh();
    },
  });
  const demo = useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/platform/console/tenants/{id}:create-demo', { params: { path: { id: tenant.id } }, body: { packCode: null } })),
  });

  const data = overview.data;
  const invoiceColumns: DataTableColumn<SubscriptionInvoiceView>[] = [
    { id: 'number', headerKey: 'console:subscription.invoices.number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    { id: 'issued', headerKey: 'console:subscription.invoices.issued', cell: (row) => <JalaliDate value={row.issuedAt} /> },
    { id: 'kind', headerKey: 'console:subscription.invoices.kind', cell: (row) => t(`console:subscription.invoice_kind.${row.kind}`) },
    { id: 'total', headerKey: 'console:subscription.invoices.total', cell: (row) => <Money amountMinor={row.total.amount} currency={row.total.currency} unit="Rial" /> },
    { id: 'status', headerKey: 'console:subscription.invoices.status', cell: (row) => t(`console:subscription.invoice_status.${row.status}`) },
  ];
  const trial = data?.status === 'Trial' || data?.status === 'TrialExpired';
  return (
    <Stack spacing={2} data-testid="console-subscription">
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t('console:subscription.title')}
        </Typography>
        {overview.isPending ? <LoadingState rows={2} /> : null}
        {overview.error ? <ErrorState error={overview.error} compact /> : null}
        {data ? (
          <Stack spacing={2}>
            <KeyValueList
              columns={3}
              items={[
                { labelKey: 'console:subscription.status', value: t(`console:subscription.states.${data.status}`), testId: 'console-subscription-status' },
                { labelKey: 'console:subscription.plan', value: <bdi dir="ltr">{data.planCode}</bdi> },
                { labelKey: 'console:subscription.cycle', value: data.cycle ? t(`console:subscription.cycles.${data.cycle}`) : '—' },
                {
                  labelKey: 'console:subscription.period',
                  value: data.periodStart ? `${toJalali(data.periodStart)} — ${toJalali(data.periodEnd)}` : '—',
                  testId: 'console-subscription-period',
                },
                { labelKey: 'console:subscription.providers', value: `${faDigits(toNumber(data.paidProviders) ?? 0)} / ${faDigits(toNumber(data.activeProviders) ?? 0)}` },
                { labelKey: 'console:subscription.access_mode', value: t(`console:access_mode.${data.accessMode}`) },
              ]}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {trial && access.can('platform.subscription.manage') ? (
                <Button variant="outlined" onClick={() => setExtendOpen(true)} data-testid="console-extend-trial">
                  {t('console:subscription.extend_trial')}
                </Button>
              ) : null}
              {tenant.status === 'Suspended' && access.can('platform.tenant.suspend') ? (
                <Button variant="outlined" color="error" onClick={() => setTerminateOpen(true)} data-testid="console-terminate">
                  {t('console:subscription.terminate')}
                </Button>
              ) : null}
              {!tenant.isDemo && tenant.status === 'Active' && access.can('platform.tenant.create') ? (
                <Button variant="outlined" onClick={() => demo.mutate()} disabled={demo.isPending} data-testid="console-create-demo">
                  {t('console:subscription.create_demo')}
                </Button>
              ) : null}
            </Stack>
            {demo.error ? <ErrorState error={demo.error} compact /> : null}
            {demo.data ? (
              <Alert severity="success" data-testid="console-demo-created">
                {t('console:subscription.demo_created', { slug: demo.data.slug })}
              </Alert>
            ) : null}
          </Stack>
        ) : null}
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t('console:subscription.invoices.title')}
        </Typography>
        <DataTable
          captionKey="console:subscription.invoices.title"
          columns={invoiceColumns}
          rows={invoices.data?.items}
          isLoading={invoices.isPending}
          error={invoices.error}
          onRetry={() => void invoices.refetch()}
          getRowId={(row) => row.id}
          emptyTitleKey="console:subscription.invoices.empty"
        />
      </Paper>
      <OwnershipApprovals tenantId={tenant.id} />
      {(certificates.data?.items ?? []).map((certificate) => (
        <Alert severity="info" key={certificate.id} data-testid="console-purge-certificate">
          {t('console:subscription.purged', {
            number: certificate.number,
            date: toJalali(certificate.purgedAt),
            rows: faDigits(toNumber(certificate.totalRows) ?? 0),
            files: faDigits(toNumber(certificate.filesDeleted) ?? 0),
          })}
          <Typography variant="caption" component="div" dir="ltr" sx={{ wordBreak: 'break-all' }}>
            SHA-256: {certificate.digest}
          </Typography>
        </Alert>
      ))}
      <ReasonDialog
        open={extendOpen}
        titleKey="console:subscription.extend_title"
        messageKey="console:subscription.extend_message"
        confirmKey="console:subscription.extend_trial"
        destructive={false}
        busy={extend.isPending}
        error={extend.error}
        onCancel={() => setExtendOpen(false)}
        onConfirm={(reason) => extend.mutate(reason)}
      >
        <TextField
          label={t('console:subscription.extend_days')}
          value={days}
          onChange={(event) => setDays(event.target.value)}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'extend-days' } }}
          sx={{ mb: 2 }}
        />
      </ReasonDialog>
      <ReasonDialog
        open={terminateOpen}
        titleKey="console:subscription.terminate_title"
        messageKey="console:subscription.terminate_message"
        confirmKey="console:subscription.terminate"
        busy={terminate.isPending}
        error={terminate.error}
        onCancel={() => setTerminateOpen(false)}
        onConfirm={(reason) => terminate.mutate(reason)}
      />
    </Stack>
  );
}
